import {computed, inject, Injectable, PLATFORM_ID, signal} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {BreakpointObserver, Breakpoints} from '@angular/cdk/layout';
import {filter, map, Observable, tap} from 'rxjs';
import {BreakPointClass} from '@core/service/browser';
import {ActivationEnd, NavigationEnd, NavigationStart, Router} from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class BrowserService {

  public readonly isBrowser: boolean;
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly router = inject(Router);
  public readonly breakpoint$: Observable<BreakPointClass>;
  private readonly _breakpoint = signal<BreakPointClass | null>(null);
  public readonly isLandscape$: Observable<boolean>;
  public readonly routeClasses$: Observable<string[]>;

  public readonly isMobile = computed(() => this._breakpoint() === BreakPointClass.xs);

  public static readonly STORAGE_KEY = {
    IsAuthenticated: 'IS_AUTHENTICATED',
    ForUser: 'FOR_USER',
    Invitation: 'invitation'
  };

  private static readonly breakpointsMap = {
    [Breakpoints.XSmall]: BreakPointClass.xs,
    [Breakpoints.Small]: BreakPointClass.sm,
    [Breakpoints.Medium]: BreakPointClass.md,
    [Breakpoints.Large]: BreakPointClass.lg,
    [Breakpoints.XLarge]: BreakPointClass.xl
  };

  public constructor() {
    const platformId = inject<string>(PLATFORM_ID);
    this.isBrowser = isPlatformBrowser(platformId);

    this.breakpoint$ = this.breakpointObserver.observe(
      Object.keys(BrowserService.breakpointsMap)
    ).pipe(
      map(result => {
        const breakpoint = Object.keys(result.breakpoints).find(query => result.breakpoints[query]);
        return breakpoint
          ? BrowserService.breakpointsMap[breakpoint]
          : BreakPointClass.xs;
      }),
      tap(breakpoint => {
        this._breakpoint.set(breakpoint);
      })
    );
    this.isLandscape$ = this.breakpointObserver.observe(
      ['(orientation: landscape)']
    ).pipe(
      map(result => Object.keys(result.breakpoints).some(query => result.breakpoints[query]))
    );
    let contentClasses: string[] = [];
    this.routeClasses$ = this.router.events.pipe(
      tap(event => {
        if (event instanceof NavigationStart) {
          contentClasses = [];
        }
        if (event instanceof ActivationEnd) {
          const segmentClasses = ((event.snapshot.data?.contentClasses ?? []) as string[]);
          if (segmentClasses.length > 0) {
            contentClasses.push(... segmentClasses);
          }
        }
      }),
      filter(event => event instanceof NavigationEnd),
      map(_ => contentClasses)
    );
  }

  public write(key: string, value: string): void {
    if (this.isBrowser) {
      window.localStorage.setItem(key, value);
    }
  }

  public read(key: string): string | null | undefined {
    return this.isBrowser ? window.localStorage.getItem(key) : undefined;
  }

  public writeObject<T>(key: string, value: T): void {
    this.write(key, JSON.stringify(value));
  }

  public readObject<T>(key: string): T | null | undefined {
    const value = this.read(key);
    return value ? JSON.parse(value) : undefined;
  }

  public delete(key: string): void {
    if (this.isBrowser) {
      window.localStorage.removeItem(key);
    }
  }

  public copyToClipboard(text: string): boolean {
    if (this.isBrowser && window?.navigator?.clipboard) {
      window.navigator.clipboard.writeText(text);
      return true;
    }
    return false;
  }
}
